if (!customElements.get('fit-media-hero-slider')) {
  class FitMediaHeroSlider extends HTMLElement {
    constructor() {
      super();
      this.currentIndex = 0;
      this.timer = null;
      this.pointerStartX = null;
      this.isEditorPaused = false;
      this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
      this.handleMediaChange = this.handleMediaChange.bind(this);
    }

    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('[data-fit-media-hero-slide]'));
      this.paginationButtons = Array.from(this.querySelectorAll('[data-fit-media-hero-dot]'));
      this.autoplayEnabled = this.dataset.autoplay === 'true' && this.slides.length > 1;
      this.delay = Math.max(Number(this.dataset.delay) || 6000, 1000);
      this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.mobileMediaQuery = window.matchMedia('(max-width: 749px)');

      if (!this.slides.length) return;

      this.paginationButtons.forEach((button) => {
        button.addEventListener('click', () => {
          this.goTo(Number(button.dataset.index), true);
        });
      });

      this.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') this.goTo(this.currentIndex + 1, true);
        if (event.key === 'ArrowLeft') this.goTo(this.currentIndex - 1, true);
      });

      this.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'mouse' || event.button === 0) {
          this.pointerStartX = event.clientX;
        }
      });

      this.addEventListener('pointerup', (event) => {
        if (this.pointerStartX === null) return;
        const distance = event.clientX - this.pointerStartX;
        this.pointerStartX = null;
        if (Math.abs(distance) < 50) return;
        this.goTo(distance < 0 ? this.currentIndex + 1 : this.currentIndex - 1, true);
      });

      this.addEventListener('pointercancel', () => {
        this.pointerStartX = null;
      });

      this.addEventListener('shopify:block:select', (event) => {
        const slide = event.target.closest('[data-fit-media-hero-slide]');
        if (!slide) return;
        this.isEditorPaused = true;
        this.goTo(Number(slide.dataset.index), false);
        this.pause();
      });

      this.addEventListener('shopify:block:deselect', () => {
        this.isEditorPaused = false;
        this.resume();
      });

      document.addEventListener('visibilitychange', this.handleVisibilityChange);
      this.mobileMediaQuery.addEventListener('change', this.handleMediaChange);
      this.goTo(0, false);
    }

    disconnectedCallback() {
      this.clearTimer();
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
      this.mobileMediaQuery?.removeEventListener('change', this.handleMediaChange);
    }

    handleVisibilityChange() {
      if (document.hidden) this.pause();
      else this.resume();
    }

    handleMediaChange() {
      this.syncActiveVideos(false);
    }

    goTo(index, restartAutoplay = false) {
      const total = this.slides.length;
      if (!total) return;

      this.currentIndex = ((index % total) + total) % total;

      this.slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === this.currentIndex;
        slide.classList.toggle('is-active', isActive);
        slide.setAttribute('aria-hidden', String(!isActive));
        if ('inert' in slide) slide.inert = !isActive;

        slide.querySelectorAll('video').forEach((video) => video.pause());
      });

      this.syncActiveVideos(true);

      this.paginationButtons.forEach((button, buttonIndex) => {
        const isActive = buttonIndex === this.currentIndex;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-current', String(isActive));
      });

      this.restartProgress();
      if (restartAutoplay) this.startTimer();
      else if (!this.timer) this.startTimer();
    }

    restartProgress() {
      const activeButton = this.paginationButtons[this.currentIndex];
      if (!activeButton) return;
      const progress = activeButton.querySelector('.fit-media-hero__progress-value');
      if (!progress) return;
      progress.style.animation = 'none';
      progress.getBoundingClientRect();
      progress.style.animation = '';
    }

    syncActiveVideos(resetVideo) {
      const activeSlide = this.slides?.[this.currentIndex];
      this.slides?.forEach((slide) => {
        slide.querySelectorAll('video').forEach((video) => {
          const shouldPlay = slide === activeSlide && getComputedStyle(video).display !== 'none';
          if (!shouldPlay) {
            video.pause();
            return;
          }
          if (resetVideo) video.currentTime = 0;
          if (!this.classList.contains('is-paused')) video.play().catch(() => {});
        });
      });
    }

    startTimer() {
      this.clearTimer();
      if (
        !this.autoplayEnabled ||
        this.prefersReducedMotion ||
        this.isEditorPaused ||
        document.hidden
      ) {
        this.classList.add('is-paused');
        return;
      }
      this.classList.remove('is-paused');
      this.timer = window.setTimeout(() => {
        this.timer = null;
        this.goTo(this.currentIndex + 1, false);
      }, this.delay);
    }

    clearTimer() {
      if (this.timer) window.clearTimeout(this.timer);
      this.timer = null;
    }

    pause() {
      this.clearTimer();
      this.classList.add('is-paused');
      const activeSlide = this.slides?.[this.currentIndex];
      activeSlide?.querySelectorAll('video').forEach((video) => video.pause());
    }

    resume() {
      if (this.isEditorPaused || document.hidden) return;
      this.classList.remove('is-paused');
      this.syncActiveVideos(false);
      this.restartProgress();
      this.startTimer();
    }
  }

  customElements.define('fit-media-hero-slider', FitMediaHeroSlider);
}
